import { MarginMetadata } from 'data/Margin/slice'
import { SideType } from 'scenes/MarketData/types'
import webSocket from 'services/api/webSocket'

import {
  RejectedMessageType,
  SnapshotMessageType,
  SubscribedMessageType,
  UnsubscribedMessageType,
  UpdatedMessageType
} from '../Socket/types'
import { MarginOrderDetailsRequest } from './actions'

export type OrderStatusType =
  | typeof webSocket.order.status.CANCELLED
  | typeof webSocket.order.status.EXPIRED
  | typeof webSocket.order.status.FILLED
  | typeof webSocket.order.status.NEW
  | typeof webSocket.order.status.OPEN
  | typeof webSocket.order.status.PARTIAL
  | typeof webSocket.order.status.PENDING
  | typeof webSocket.order.status.REJECTED
  | typeof webSocket.order.status.PART_FILLED

export type OrderStyleType =
  | typeof webSocket.order.type.LIMIT
  | typeof webSocket.order.type.MARKET
  | typeof webSocket.order.type.STOP
  | typeof webSocket.order.type.STOP_LIMIT

export type OrderTifType =
  | typeof webSocket.order.tif.FOK
  | typeof webSocket.order.tif.GTC
  | typeof webSocket.order.tif.GTD
  | typeof webSocket.order.tif.IOC

export type OrderType = {
  avgPx?: number
  clOrdID: string
  collateralAmount?: number
  collateralCurrency?: string
  created?: string
  cumQty: number // filled amount
  execInst?: string
  fee?: number
  id?: string
  lastShares?: number
  leavesQty: number // remaining amount
  leverageRatio?: number
  marginOrder: boolean
  maxOrder?: boolean
  ordStatus: OrderStatusType
  ordType: OrderStyleType
  orderID: string
  orderQty: number // total amount
  positionId?: number
  price: number
  sendingTime: string
  side: SideType
  stopPx?: number
  symbol: string
  text?: string
  timeInForce: OrderTifType
  transactTime?: string
  transactionTime?: number
}

export type HistoricalOrderType = OrderType & {
  transactionTime: number
}

export type LiveOrderType = OrderType & {
  transactTime: string
}

export type FilledType = {
  amountRemaining: number
  execId: number
  fee: number
  gwOrderId: number
  orderID: number
  price: number
  quantity: number
  side: SideType
  symbol: string
  tradeId?: number
  transactionTime: number
}

export enum PositionStatus {
  CLOSED = 'closed',
  IN_LIQUIDATION = 'in_liquidation',
  LIQUIDATED = 'liquidated',
  OPEN = 'open'
}

export type Position = {
  avgPx: number
  clOrdID: string
  collateralAmount: number
  collateralCurrency: string
  execID: number
  fee: number
  interestAmount: number
  leavesQty: number
  leverageRatio: number
  liquidationPrice: number
  marginCallPrice: number
  markPrice: number
  orderID: string
  positionId: number
  qty: number
  realizedPnl: number
  side: SideType
  status: PositionStatus
  stopLoss: number
  symbol: string
  takeProfit?: number
  transactTime: number
}

export type OrderRejectedType = {
  readonly clOrdID: string
  readonly text: string
}

type OrderDeltaType = {
  readonly orders: LiveOrderType[]
  readonly positions: Position[]
}

export type RejectedOrderMessageType = OrderRejectedType & RejectedMessageType

export type SnapshotOrderMessageType = OrderDeltaType & SnapshotMessageType

export type UpdateOrderMessageType = OrderType & UpdatedMessageType

export type TradingSubscribedMessage = SubscribedMessageType & MarginMetadata

export type OrderMessageType =
  | SnapshotOrderMessageType
  | UpdateOrderMessageType
  | RejectedOrderMessageType
  | SubscribedMessageType
  | UnsubscribedMessageType

export type CreateOrderMessageType = {
  readonly action: typeof webSocket.action.CREATE | typeof webSocket.action.CREATE_MARGIN
  readonly channel: typeof webSocket.channel.TRADING
  readonly cumQty: any
  readonly event: any
  readonly leavesQty: any
  readonly ordStatus: any
  readonly orderID: any
  readonly orderQty: number
  readonly price?: number
  readonly sendingTime: any
}

export type GetMarginPositionMessageType = {
  readonly action: typeof webSocket.action.CREATE
  readonly channel: typeof webSocket.channel.TRADING
  readonly requestId: string
} & MarginOrderDetailsRequest

export type GetMarginCallDetailsMessage = {
  action: typeof webSocket.action.MARGIN_CALL_DETAILS
  channel: typeof webSocket.channel.TRADING
  positionId: string
  requestId: string
}

export type CancelOrderMessageType = {
  readonly action: typeof webSocket.action.CANCEL
  readonly channel: typeof webSocket.channel.TRADING
  readonly orderID: string
}

export type MarginOrderDetails = {
  readonly bankruptcyPrice: number
  readonly callPrice: number
  readonly collateralAmount: number
  readonly collateralValue: number
  readonly liquidationPrice: number
  readonly markPrice: number
}

export type MarginCallDetails = {
  marginCallTopUpAmount: number
  positionId: string
  removableCollateralAmount: number
}
